import { clsx } from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';

import { useVisualViewportHeight } from '../../hooks/useVisualViewportHeight/useVisualViewportHeight';
import {
  getColorCSSVar,
  getColorForBackgroundCSSVar,
} from '../../utils/colors';
import { shadowApp, shadowAppPortal } from '../../utils/shadowRoot';
import type { ModalProps } from './modal.types';
import { ModalContent } from './modalContent/modalContent';
import { ModalContentDivider } from './modalContent/modalContentDivider';
import { ModalContext } from './modalContext';
import { ModalFooter } from './modalFooter/modalFooter';
import { ModalHeader } from './modalHeader/modalHeader';

import styles from './modal.module.css';

const shadowAppSelector = (): HTMLElement => {
  if (typeof document === 'undefined') {
    /**
     * Guard for server-side rendering.
     * > The Document.body property represents the <body> or <frameset> node of the current document, or null if no such element exists.
     */
    return null as unknown as HTMLElement;
  }

  return shadowAppPortal() || document.body;
};

/**
 * Element react-modal sets `aria-hidden` on while open. Inside a shadow root
 * (Boost frameless embed) this must be `#shadow-app`: the host page may own
 * its own `#app` that wraps the shadow host, and hiding it hides the modal too.
 */
const appElementSelector = (appId: string) => {
  return shadowApp() || document.getElementById(appId);
};

/**
 * `Modal` component that allows for simple dialogue modals, complex modals
 * with headers and footers, or full-screen modals.
 */
function BaseModal({
  appId = 'app',
  ariaLabelledBy,
  background,
  centerByVisualViewport = false,
  children,
  className,
  contentLabel,
  'data-testid': testId,
  isLayered,
  onAfterClose,
  onAfterOpen,
  onRequestClose,
  open,
  overflow = 'hidden',
  overlayClassName,
  overlayClose,
  parentId = 'parent',
  parentSelector,
  portalClassName,
  style,
  shouldReturnFocusAfterClose = true,
  topOverride,
  variant = 'standard',
}: ModalProps) {
  if (variant === 'full-screen' && !background) {
    background = 'grays-ultralight';
  } else if (!background) {
    background = 'grays-white';
  }

  const [appElement, setAppElement] = useState<HTMLElement | undefined>(
    undefined
  );

  const { keyboardHeight } = useVisualViewportHeight();
  /** Only offset by keyboard height when centerByVisualViewport=true */
  const onScreenKeyboardHeight = centerByVisualViewport ? keyboardHeight : 0;

  const contextValue = useMemo(
    () => ({ onRequestClose, variant }),
    [onRequestClose, variant]
  );

  useEffect(() => {
    // SSR compatibility: Disallow use of DOM globals in the render-cycle of a React FC, as this will break SSR if you're rendering this component.
    // To fix it, move this statement into a useEffect())
    const element = appElementSelector(appId);

    if ((!appElement || !appElement?.id) && element) {
      setAppElement(element);
    }
    if (appElement && !appElement?.id && !element) {
      setAppElement(undefined);
    }
  }, [appId, appElement]);

  return (
    <ModalContext.Provider value={contextValue}>
      <ReactModal
        appElement={appElement}
        aria={ariaLabelledBy ? { labelledby: ariaLabelledBy } : undefined}
        /**
         * Use of DOM global `document` is forbidden in the render-cycle of a React FC.
         * This workaround avoids ReactModal's warning message in tests, when appElement is undefined.
         * @see https://reactcommunity.org/react-modal/accessibility/#app-element
         */
        ariaHideApp={!!appElement}
        className={clsx(className, styles[variant], [
          styles['ReactModal__Content'],
        ])}
        closeTimeoutMS={200}
        contentLabel={contentLabel}
        data-testid={testId}
        id={parentId}
        isOpen={open}
        onAfterOpen={() => {
          document.body.style.overflow = 'hidden';
          onAfterOpen?.();
        }}
        onAfterClose={() => {
          if (!isLayered) {
            document.body.style.overflow = 'unset';
          }
          onAfterClose?.();
        }}
        onRequestClose={onRequestClose}
        overlayClassName={clsx(
          styles['ReactModal__Overlay'],
          {
            [styles['ReactModal__Overlay--isOpen']]: open,
            [styles['ReactModal__Overlay--isClosed']]: !open,
          },
          overlayClassName
        )}
        parentSelector={parentSelector || shadowAppSelector}
        portalClassName={portalClassName}
        shouldCloseOnOverlayClick={overlayClose ? true : false}
        shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
        style={{
          content: {
            '--keyboard-height': `${onScreenKeyboardHeight}px`,
            '--modal-background-color': getColorCSSVar(background),
            '--modal-color': getColorForBackgroundCSSVar(background),
            '--modal-overflow': overflow,
            '--modal-top': topOverride
              ? topOverride
              : `calc(50% - ${onScreenKeyboardHeight / 2}px)`,
            '--modal-transform': topOverride
              ? 'translateX(-50%)'
              : 'translate(-50%, -50%)',
            ...style,
          },
        }}
      >
        {children}
      </ReactModal>
    </ModalContext.Provider>
  );
}

export const Modal = Object.assign(BaseModal, {
  Header: ModalHeader,
  Content: ModalContent,
  ContentDivider: ModalContentDivider,
  Footer: ModalFooter,
});
